import React, { useState, useRef, useEffect } from 'react';
import { MdArrowForward } from 'react-icons/md';
import ProtectedLink from '../ProtectedLink';
import image67 from '../../assets/image67.png';
import { useCartStore } from '../../store/cartStore';
import { useAuthStore } from '../../store/authStore';
import { listSkus, Sku } from '../../services/skusApi';
import AddToCartButton from '../shared/AddToCartButton';
import '../../styles/home/MovingNowSection.css';

type FilterTab = 'trending' | 'best-seller' | 'new-arrival';

interface FeaturedProduct {
  id: number;
  badge: string;
  badgeColor: string;
  category: string;
  name: string;
  brand: string;
  price: string;
  image: string;
}

const allProducts: Record<FilterTab, FeaturedProduct[]> = {
  trending: [
    { id: 1, badge: 'TRENDING', badgeColor: '#3B6EF6', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 2, badge: 'TRENDING', badgeColor: '#3B6EF6', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 3, badge: 'TRENDING', badgeColor: '#3B6EF6', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 4, badge: 'TRENDING', badgeColor: '#3B6EF6', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 5, badge: 'TRENDING', badgeColor: '#3B6EF6', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
  ],
  'best-seller': [
    { id: 6, badge: 'BEST SELLER', badgeColor: '#475569', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 7, badge: 'BEST SELLER', badgeColor: '#475569', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 8, badge: 'BEST SELLER', badgeColor: '#475569', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 9, badge: 'BEST SELLER', badgeColor: '#475569', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 10, badge: 'BEST SELLER', badgeColor: '#475569', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
  ],
  'new-arrival': [
    { id: 11, badge: 'NEW ARRIVAL', badgeColor: '#059669', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 12, badge: 'NEW ARRIVAL', badgeColor: '#059669', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 13, badge: 'NEW ARRIVAL', badgeColor: '#059669', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 14, badge: 'NEW ARRIVAL', badgeColor: '#059669', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
    { id: 15, badge: 'NEW ARRIVAL', badgeColor: '#059669', category: 'BEVERAGES', name: 'Beer 12oz Can', brand: 'Brand Name', price: '$18.50', image: image67 },
  ],
};

const tabs: { key: FilterTab; label: string }[] = [
  { key: 'trending', label: 'TRENDING' },
  { key: 'best-seller', label: 'BEST SELLER' },
  { key: 'new-arrival', label: 'NEW ARRIVAL' },
];

const BADGE_BY_TAB: Record<FilterTab, { label: string; color: string }> = {
  trending: { label: 'TRENDING', color: '#3B6EF6' },
  'best-seller': { label: 'BEST SELLER', color: '#475569' },
  'new-arrival': { label: 'NEW ARRIVAL', color: '#059669' },
};

const MovingNowSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FilterTab>('trending');
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [skus, setSkus] = useState<Sku[] | null>(null);

  // Categories/SKUs require auth; on the public home page we show the static
  // teaser when logged out and live products once signed in.
  useEffect(() => {
    if (!isAuthenticated) {
      setSkus(null);
      return;
    }
    let cancelled = false;
    listSkus({ limit: 12 })
      .then((page) => {
        if (!cancelled && page.data.length) setSkus(page.data);
      })
      .catch(() => {
        /* keep static fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const products: FeaturedProduct[] = skus
    ? skus.map((s) => ({
        id: s.id,
        badge: BADGE_BY_TAB[activeTab].label,
        badgeColor: BADGE_BY_TAB[activeTab].color,
        category: (s.category_name ?? 'PRODUCT').toUpperCase(),
        name: s.product_name,
        brand: '',
        price: `$${(s.list_price ?? s.unit_cost ?? 0).toFixed(2)}`,
        image: s.image_url || image67,
      }))
    : allProducts[activeTab];

  const addToCart = useCartStore((s) => s.addItem);

  return (
    <section className="moving-now">
      <div className="moving-now__container">
        {/* Header row */}
        <div className="moving-now__header">
          <div>
            <span className="moving-now__tag">FEATURED PRODUCTS</span>
            <h2 className="moving-now__heading">What's moving now</h2>
          </div>
          <ProtectedLink to="/products" className="moving-now__scroll-link">
            SCROLL TO EXPLORE <MdArrowForward />
          </ProtectedLink>
        </div>

        {/* Filter tabs */}
        <div className="moving-now__tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`moving-now__tab ${activeTab === tab.key ? 'moving-now__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Product cards - horizontal scroll */}
        <div className="moving-now__scroll" ref={scrollRef}>
          {products.map((product) => (
            <div key={product.id} className="moving-now__card">
              <div className="moving-now__card-image-wrap">
                <span
                  className="moving-now__card-badge"
                  style={{ background: product.badgeColor }}
                >
                  {product.badge}
                </span>
                <img
                  src={product.image}
                  alt={product.name}
                  className="moving-now__card-image"
                  loading="lazy"
                />
              </div>
              <div className="moving-now__card-info">
                <span className="moving-now__card-category">{product.category}</span>
                <h3 className="moving-now__card-name">{product.name}</h3>
                <span className="moving-now__card-brand">{product.brand}</span>
                <div className="moving-now__card-footer">
                  <span className="moving-now__card-price">{product.price}</span>
                  <AddToCartButton
                    size="sm"
                    onClick={() => {
                      addToCart({
                        id: product.id, name: product.name, slug: String(product.id),
                        price: parseFloat(product.price.replace('$', '')),
                        description: '', shortDescription: '', images: [product.image],
                        category: product.category, brand: product.brand,
                        rating: 5, reviewCount: 0, inStock: true, stockCount: 10,
                        tags: [], features: [], sku: '',
                      }, 1);
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MovingNowSection;
